import { useLocation } from 'react-router-dom';

import { findModuleByPath } from '@/app/modules';
import { useTheme } from '@/app/ThemeProvider';
import { Avatar, Icon, IconButton } from '@/components/ui';
import { useProfile } from '@/features/account/hooks';
import { useAuth } from '@/features/auth/AuthProvider';

import styles from './Topbar.module.css';

interface TopbarProps {
  onOpenMenu: () => void;
  sidebarOpen: boolean;
}

export function Topbar({ onOpenMenu, sidebarOpen }: TopbarProps) {
  const { pathname } = useLocation();
  const { user, signOut, signingOut } = useAuth();
  const { profile, name } = useProfile();
  const { resolved, toggle } = useTheme();

  const module = findModuleByPath(pathname);

  // The secondary line only earns its place when it says something the name
  // does not already say.
  const email = profile?.email ?? user?.email ?? null;
  const secondary = email !== null && email !== name ? email : null;

  return (
    <header className={styles.topbar}>
      <IconButton
        icon="menu"
        label="Open navigation"
        className={styles.menuButton}
        aria-expanded={sidebarOpen}
        aria-controls="ela-sidebar"
        onClick={onOpenMenu}
      />

      <div className={styles.crumb}>
        {module ? (
          <>
            <Icon name={module.icon} size={15} className={styles.crumbIcon} />
            <span>{module.label}</span>
          </>
        ) : null}
      </div>

      <div className={styles.spacer} />

      <div className={styles.actions}>
        <IconButton
          icon={resolved === 'dark' ? 'sun' : 'moon'}
          label={resolved === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          onClick={toggle}
        />

        <div className={styles.account}>
          <Avatar name={name} imageId={profile?.profileImageId} size="sm" />
          <span className={styles.identity}>
            <span className={styles.name}>{name}</span>
            {secondary !== null ? <span className={styles.email}>{secondary}</span> : null}
          </span>
          <IconButton
            icon="logout"
            label="Sign out"
            disabled={signingOut}
            onClick={() => void signOut()}
          />
        </div>
      </div>
    </header>
  );
}
